/* -------------------------------------------------------------------------- */
/*  MESSAGE DELIVERY STATUS                                                   */
/*                                                                            */
/*  WhatsApp-style ticks for messages I sent, plus the read-receipt plumbing  */
/*  and the send-with-retry outbox both chat screens (direct + team) share.   */
/*  Ported from the mobile app's components/chat/messageStatus.tsx so both    */
/*  clients read/write the exact same RTDB shape.                             */
/*                                                                            */
/*  Receipts are stored as per-user high-water marks, not per message:        */
/*                                                                            */
/*    <basePath>/receipts/<uid> = { deliveredUpTo: ms, readUpTo: ms }         */
/*                                                                            */
/*  where basePath is the node that owns `messages` — the conversation node   */
/*  for a DM, the channel node for a team chat. A mark says "every message    */
/*  created at or before this timestamp has reached / been seen by uid", so a */
/*  participant writes one small value per visit instead of one write per     */
/*  message, and the sender needs a single listener to tick a whole screenful.*/
/*  Marks only ever move forward (see bumpReceipt), so an out-of-order write   */
/*  from a backgrounded client can't un-read a message.                       */
/* -------------------------------------------------------------------------- */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, HStack, Text } from "@chakra-ui/react";
import { BsCheck2, BsCheck2All, BsClockHistory, BsExclamationCircleFill } from "react-icons/bs";
import {
  ref as dbRef,
  onValue,
  off,
  get,
  push,
  set,
  query,
  orderByKey,
  limitToLast,
  runTransaction,
} from "firebase/database";
import { rtdb } from "../../firebase/firebase";
import { ChakraIcon } from "../ChakraIcon";

/** Where a message sits between "the user hit send" and "everyone read it". */
export type SendState = "sending" | "failed" | "sent";
export type TickState = SendState | "delivered" | "read";

export type Receipts = Record<
  string,
  { deliveredUpTo?: number; readUpTo?: number } | null | undefined
>;

/* -------------------------------------------------------------------------- */
/*  WRITES                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Raise one of my marks. A transaction rather than a plain set so a stale
 * value (an old tab settling after a newer one already marked read) can
 * never pull the mark backwards.
 */
export async function bumpReceipt(
  basePath: string | null | undefined,
  uid: string | null | undefined,
  field: "deliveredUpTo" | "readUpTo",
  ts: number,
): Promise<void> {
  if (!basePath || !uid || !ts) return;
  try {
    await runTransaction(
      dbRef(rtdb, `${basePath}/receipts/${uid}/${field}`),
      (cur) => (typeof cur === "number" && cur >= ts ? cur : ts),
    );
  } catch (e) {
    // Receipts are cosmetic: never let one break sending or reading.
    console.warn("[messageStatus] bumpReceipt failed:", e);
  }
}

/** createdAt of the newest message stored under basePath, or 0. */
export async function newestMessageTs(basePath: string): Promise<number> {
  try {
    const snap = await get(
      query(dbRef(rtdb, `${basePath}/messages`), orderByKey(), limitToLast(1)),
    );
    let ts = 0;
    snap.forEach((child) => {
      const v = child.val();
      if (typeof v?.createdAt === "number" && v.createdAt > ts) ts = v.createdAt;
    });
    return ts;
  } catch {
    return 0;
  }
}

/**
 * "It reached my device." Called from the chat list the moment an unread
 * message lands, so the sender sees two grey ticks without the recipient
 * having to open the thread. Reads the newest key rather than trusting
 * Date.now(), so a skewed device clock can't mark future messages delivered.
 */
export async function markDelivered(
  basePath: string | null | undefined,
  uid: string | null | undefined,
): Promise<void> {
  if (!basePath || !uid) return;
  const ts = await newestMessageTs(basePath);
  if (ts) await bumpReceipt(basePath, uid, "deliveredUpTo", ts);
}

/* -------------------------------------------------------------------------- */
/*  READS                                                                     */
/* -------------------------------------------------------------------------- */

/** Live receipts for everyone in this conversation/channel. */
export function useReceipts(basePath: string | null | undefined): Receipts {
  const [receipts, setReceipts] = useState<Receipts>({});
  useEffect(() => {
    if (!basePath) {
      setReceipts({});
      return;
    }
    const node = dbRef(rtdb, `${basePath}/receipts`);
    const cb = (snap: any) => setReceipts(snap.val() || {});
    onValue(node, cb);
    return () => off(node, "value", cb);
  }, [basePath]);
  return receipts;
}

/**
 * Publish my own marks while this chat is the one open. `newestTs` is the
 * newest message currently rendered, so opening a thread marks its whole
 * backlog read in one write and each arriving message costs one more.
 */
export function usePublishReceipts(
  basePath: string | null | undefined,
  myUid: string | null | undefined,
  newestTs: number,
  active: boolean,
): void {
  const highWater = useRef(0);
  // A different thread starts its own high-water mark.
  useEffect(() => {
    highWater.current = 0;
  }, [basePath, myUid]);

  useEffect(() => {
    if (!basePath || !myUid || !newestTs || !active) return;
    if (highWater.current >= newestTs) return;
    highWater.current = newestTs;
    void bumpReceipt(basePath, myUid, "deliveredUpTo", newestTs);
    void bumpReceipt(basePath, myUid, "readUpTo", newestTs);
  }, [basePath, myUid, newestTs, active]);
}

/* -------------------------------------------------------------------------- */
/*  OUTBOX (send + retry)                                                     */
/* -------------------------------------------------------------------------- */

/**
 * How long to wait for the server to acknowledge a write before calling it
 * failed.
 *
 * A timeout, not just a rejection, because of how the Realtime Database
 * behaves offline: `set()` writes to the local cache, fires child_added
 * immediately, and leaves its promise pending until the socket comes back —
 * it never rejects for "no network". Without a deadline a message sent on a
 * dead or slow connection would sit there looking sent forever.
 */
export const SEND_TIMEOUT_MS = 12000;

export function withSendTimeout<T>(p: Promise<T>, ms = SEND_TIMEOUT_MS): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("send-timeout")), ms);
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

interface OutboxEntry {
  id: string;
  msg: any;
  state: SendState;
  onSent?: () => void;
  /** Bumped on every (re)send so a stale attempt can't clobber a newer one. */
  attempt?: number;
}

export interface MessageOutbox {
  /** Entries still in flight or failed, keyed by message id. */
  entries: Record<string, OutboxEntry>;
  /**
   * Write a message, remembering it so a failure can be retried. `build`
   * receives the generated push key, since some payloads carry their own id.
   * Resolves true once the server has acknowledged the write.
   */
  send: (build: (id: string) => any, onSent?: () => void) => Promise<boolean>;
  /** Re-attempt a failed send. Same key, so a late queued write can't duplicate it. */
  retry: (id: string) => void;
  /** Give up on a failed send and drop it from the thread. */
  discard: (id: string) => void;
}

/* ---------------------------------------------------------------------- */
/*  Module-level store, keyed by messages path.                            */
/*                                                                          */
/*  A WhatsApp-style pending/failed message has to survive the user        */
/*  leaving the thread and coming back — React state scoped to the open    */
/*  chat screen does not: it is thrown away the moment the component       */
/*  unmounts. Keeping the entries here instead means re-opening the same   */
/*  thread finds them exactly as they were, and a global reconnect watcher */
/*  can retry a failed send even if nobody is currently looking at it.     */
/* ---------------------------------------------------------------------- */
const outboxStore = new Map<string, Record<string, OutboxEntry>>();
const outboxListeners = new Map<string, Set<() => void>>();

function getStoreEntries(path: string | null | undefined): Record<string, OutboxEntry> {
  if (!path) return {};
  return outboxStore.get(path) || {};
}

function setStoreEntries(path: string, next: Record<string, OutboxEntry>): void {
  if (Object.keys(next).length === 0) outboxStore.delete(path);
  else outboxStore.set(path, next);
  outboxListeners.get(path)?.forEach((l) => l());
}

function subscribeStore(path: string, listener: () => void): () => void {
  let set = outboxListeners.get(path);
  if (!set) {
    set = new Set();
    outboxListeners.set(path, set);
  }
  set.add(listener);
  return () => {
    set!.delete(listener);
    if (set!.size === 0) outboxListeners.delete(path);
  };
}

/**
 * Write (or re-write) one message and settle its outbox entry. Standalone
 * rather than a hook method, so the reconnect watcher below can drive it for
 * a thread nobody has open.
 *
 * Retrying reuses the original push key and re-writes the same path, so it
 * is idempotent: a first attempt that was merely slow, not actually failed,
 * overwrites itself with identical content instead of posting twice.
 */
async function deliverEntry(path: string, id: string): Promise<boolean> {
  const seed = getStoreEntries(path)[id];
  if (!seed) return false;
  const attempt = (seed.attempt || 0) + 1;
  const entry: OutboxEntry = { ...seed, state: "sending", attempt };
  setStoreEntries(path, { ...getStoreEntries(path), [id]: entry });

  const realWrite = set(dbRef(rtdb, `${path}/${id}`), entry.msg);

  const settle = (ok: boolean) => {
    const now = getStoreEntries(path);
    const current = now[id];
    // A newer retry (or a discard) has already moved past this attempt —
    // let that one speak for the message instead.
    if (!current || current.attempt !== attempt) return;
    if (ok) {
      const next = { ...now };
      delete next[id];
      setStoreEntries(path, next);
      entry.onSent?.();
    } else {
      setStoreEntries(path, { ...now, [id]: { ...current, state: "failed" } });
    }
  };

  // RTDB queues a write made while offline and flushes it once the socket
  // reconnects — that can resolve long after the timeout below has already
  // given up and shown "failed". Keep watching the real promise so a late
  // success still clears the message instead of leaving it stuck looking
  // failed even though it actually went out.
  realWrite.then(() => settle(true), () => {});

  try {
    await withSendTimeout(realWrite);
    settle(true);
    return true;
  } catch (e) {
    console.warn("[messageStatus] send failed, retryable:", e);
    settle(false);
    return false;
  }
}

/**
 * Retry every failed send, across every thread this tab has touched, the
 * moment the client reconnects — not just the one the user happens to be
 * looking at. Started lazily (once) the first time a chat screen mounts.
 */
let reconnectWatcherStarted = false;
function ensureReconnectWatcher(): void {
  if (reconnectWatcherStarted) return;
  reconnectWatcherStarted = true;
  let wasConnected = true; // don't treat the initial connect as a "reconnect"
  onValue(dbRef(rtdb, ".info/connected"), (snap) => {
    const connected = snap.val() === true;
    if (connected && !wasConnected) {
      outboxStore.forEach((bucket, path) => {
        Object.values(bucket).forEach((entry) => {
          if (entry.state === "failed") void deliverEntry(path, entry.id);
        });
      });
    }
    wasConnected = connected;
  });
}

/** Tracks the messages this thread has sent but not yet seen acknowledged. */
export function useMessageOutbox(messagesPath: string | null | undefined): MessageOutbox {
  ensureReconnectWatcher();

  const pathRef = useRef(messagesPath);
  pathRef.current = messagesPath;

  // Re-render whenever this path's bucket changes, whether the change came
  // from this hook instance, a previous mount of the same thread, or the
  // reconnect watcher retrying in the background.
  const [, bump] = useState(0);
  useEffect(() => {
    if (!messagesPath) return;
    return subscribeStore(messagesPath, () => bump((n) => n + 1));
  }, [messagesPath]);

  const entries = getStoreEntries(messagesPath);
  // Mirrors `entries` for the async paths: deliver() reads it after an await,
  // where a state snapshot would be stale.
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  const write = useCallback((next: Record<string, OutboxEntry>) => {
    const path = pathRef.current;
    if (path) setStoreEntries(path, next);
  }, []);

  const deliver = useCallback((id: string): Promise<boolean> => {
    const path = pathRef.current;
    return path ? deliverEntry(path, id) : Promise.resolve(false);
  }, []);

  const send = useCallback(
    (build: (id: string) => any, onSent?: () => void) => {
      const path = pathRef.current;
      if (!path) return Promise.resolve(false);
      // push() with no value only mints a key locally — nothing is written
      // until deliver() sets it.
      const id = push(dbRef(rtdb, path)).key!;
      write({
        ...entriesRef.current,
        [id]: { id, msg: build(id), state: "sending", onSent },
      });
      return deliver(id);
    },
    [deliver, write],
  );

  const retry = useCallback(
    (id: string) => {
      if (entriesRef.current[id]) void deliver(id);
    },
    [deliver],
  );

  const discard = useCallback(
    (id: string) => {
      const next = { ...entriesRef.current };
      delete next[id];
      write(next);
    },
    [write],
  );

  return useMemo(
    () => ({ entries, send, retry, discard }),
    [entries, send, retry, discard],
  );
}

/* -------------------------------------------------------------------------- */
/*  DERIVATION                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Which tick a message of mine should show.
 *
 * `trackRead` is what separates the two modules: a direct chat has one
 * reader, so "they read it" is unambiguous and earns the blue double tick. A
 * team channel has many, and the app deliberately stops at grey double ticks
 * there.
 */
export function deriveTickState(opts: {
  sendState?: SendState;
  createdAt: number;
  receipts: Receipts;
  peerUids: string[];
  trackRead: boolean;
}): TickState {
  const { sendState, createdAt, receipts, peerUids, trackRead } = opts;
  if (sendState === "sending" || sendState === "failed") return sendState;

  let delivered = false;
  let readByAny = false;
  let readByAll = peerUids.length > 0;

  for (const uid of peerUids) {
    const r = receipts?.[uid] || {};
    const readUpTo = r.readUpTo || 0;
    // Having read something implies having received it, even if the delivered
    // mark never got written (recipient went straight into the thread).
    const deliveredUpTo = Math.max(r.deliveredUpTo || 0, readUpTo);
    if (deliveredUpTo >= createdAt) delivered = true;
    if (readUpTo >= createdAt) readByAny = true;
    else readByAll = false;
  }

  if (trackRead && readByAll) return "read";
  if (delivered || readByAny) return "delivered";
  return "sent";
}

/* -------------------------------------------------------------------------- */
/*  UI                                                                        */
/* -------------------------------------------------------------------------- */

const READ_BLUE = "#53BDEB";
const FAIL_RED = "#EF4444";

export const MessageTicks = React.memo(function MessageTicks({
  state,
  tint = "currentColor",
  onRetry,
  onDiscard,
}: {
  state: TickState;
  /** Colour for the neutral ticks; the read state overrides it with blue. */
  tint?: string;
  onRetry?: () => void;
  /** Right-click escape hatch for a send that keeps failing. */
  onDiscard?: () => void;
}) {
  if (state === "failed") {
    return (
      <HStack
        spacing="3px"
        cursor={onRetry ? "pointer" : "default"}
        onClick={onRetry}
        onContextMenu={(e) => {
          if (onDiscard) {
            e.preventDefault();
            onDiscard();
          }
        }}
        title="Message failed to send. Click to retry, right-click to discard."
        role="button"
        aria-label="Message failed to send. Click to retry, right-click to discard."
      >
        <ChakraIcon as={BsExclamationCircleFill} boxSize="11px" color={FAIL_RED} />
        <Text fontSize="10px" color={FAIL_RED} fontWeight="semibold" whiteSpace="nowrap">
          Tap to retry
        </Text>
      </HStack>
    );
  }
  if (state === "sending") {
    return <ChakraIcon as={BsClockHistory} boxSize="11px" color={tint} aria-label="Sending" />;
  }
  if (state === "sent") {
    return <ChakraIcon as={BsCheck2} boxSize="13px" color={tint} aria-label="Sent" />;
  }
  return (
    <ChakraIcon
      as={BsCheck2All}
      boxSize="13px"
      color={state === "read" ? READ_BLUE : tint}
      aria-label={state === "read" ? "Read" : "Delivered"}
    />
  );
});

/**
 * The time + tick line at the foot of a bubble. Replaces the bare timestamp
 * `Text` both modules used to render.
 */
export const MessageMeta = React.memo(function MessageMeta({
  timeLabel,
  state,
  tint,
  onRetry,
  onDiscard,
}: {
  timeLabel: string;
  /** Omitted for messages I didn't send — they get no ticks at all. */
  state?: TickState | null;
  tint?: string;
  onRetry?: () => void;
  onDiscard?: () => void;
}) {
  return (
    <HStack spacing="3px" justify="flex-end" mt={1}>
      <Text fontSize="9px" opacity={0.7} whiteSpace="nowrap">
        {timeLabel}
      </Text>
      {state ? <MessageTicks state={state} tint={tint} onRetry={onRetry} onDiscard={onDiscard} /> : null}
    </HStack>
  );
});

/**
 * The tick on a chat-list row, WhatsApp style: shown only when the newest
 * message in the conversation/channel is mine. Mounted per row, so it opens
 * exactly one receipts listener for rows that need it.
 *
 * With `peerUid` (a direct chat) a blue double tick means they read it.
 * Without it (a team channel) any other member's mark counts and the tick
 * stops at grey.
 */
export const ChatRowTicks = React.memo(function ChatRowTicks({
  basePath,
  myUid,
  peerUid,
  createdAt,
}: {
  basePath: string;
  myUid: string;
  peerUid?: string;
  createdAt: number;
}) {
  const receipts = useReceipts(basePath);
  // The list's `createdAt` comes from the chat index / channel doc, which is
  // stamped a moment *after* the message was written, so it can sit past a
  // receipt mark that already covers the message. Compare against the
  // message's own timestamp instead.
  const [msgTs, setMsgTs] = useState(0);
  useEffect(() => {
    let cancelled = false;
    newestMessageTs(basePath).then((ts) => {
      if (!cancelled) setMsgTs(ts);
    });
    return () => {
      cancelled = true;
    };
  }, [basePath, createdAt]);
  const peers = peerUid
    ? [peerUid]
    : Object.keys(receipts).filter((uid) => uid !== myUid);
  const state = deriveTickState({
    createdAt: msgTs || createdAt,
    receipts,
    peerUids: peers,
    trackRead: !!peerUid,
  });
  return (
    <Box flexShrink={0}>
      <MessageTicks state={state} tint="#8B94A6" />
    </Box>
  );
});
